import { AuthenticationDetails, CognitoUser, CognitoUserPool, } from 'amazon-cognito-identity-js';
import { AuthError, } from '../types/auth';
import { HostTokenManager } from '../utils/hostTokenManager';
import { createTokenStorage } from '../utils/storage';
import { decodeToken, extractUserInfo, isTokenExpired, } from '../utils/token';
/**
 * Main authentication client for interacting with Cognito
 */
export class AuthClient {
    constructor(config) {
        Object.defineProperty(this, "userPool", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "config", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "storage", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "refreshTimer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "pendingNewPasswordUser", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Introspection state
        Object.defineProperty(this, "introspectionTimer", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "lastActivity", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: Date.now()
        });
        Object.defineProperty(this, "isVisible", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: true
        });
        Object.defineProperty(this, "activityListeners", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: []
        });
        Object.defineProperty(this, "visibilityHandler", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Event callbacks
        Object.defineProperty(this, "onAuthStateChange", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Instance tracking
        Object.defineProperty(this, "instanceId", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        // Host seamless auth mode
        Object.defineProperty(this, "hostSeamlessAuthMode", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        // Host token caching
        Object.defineProperty(this, "hostTokenManager", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        this.instanceId = Math.random().toString(36).substr(2, 9);
        this.config = {
            tokenStorage: 'localStorage',
            autoRefresh: true,
            refreshThreshold: 300,
            introspection: {
                enabled: true,
                interval: 300,
                onActivity: true,
                onVisibilityChange: true,
                onApiCall: false,
            },
            ...config,
        };
        this.onAuthStateChange = this.config.onAuthStateChange;
        // Check if host seamless auth mode is enabled
        this.hostSeamlessAuthMode =
            !!this.config.hostSeamlessAuth?.getTokenCallback;
        if (this.hostSeamlessAuthMode) {
            console.debug('AuthClient: Host seamless auth mode enabled');
        }
        else {
            console.debug('AuthClient: Cognito mode enabled');
        }
        this.userPool = new CognitoUserPool({
            UserPoolId: this.config.userPoolId,
            ClientId: this.config.clientId,
        });
        this.storage = createTokenStorage(this.config.tokenStorage);
        if (this.config.autoRefresh) {
            this.startAutoRefresh();
        }
        if (this.config.introspection?.enabled) {
            this.startIntrospection();
        }
        if (this.hostSeamlessAuthMode &&
            this.config.hostSeamlessAuth?.getTokenCallback) {
            this.hostTokenManager = new HostTokenManager({
                storage: this.storage,
                storageKey: 'hostAccessToken',
                refreshThreshold: this.config.refreshThreshold ?? 300,
                getToken: this.config.hostSeamlessAuth.getTokenCallback,
                onError: this.config.hostSeamlessAuth.onTokenError,
                debugLogging: this.config.debugLogging ?? false,
            });
        }
    }
    /**
     * Get the instance ID for debugging purposes
     */
    getInstanceId() {
        return this.instanceId;
    }
    /**
     * Authenticate user with username and password
     */
    async login(credentials) {
        if (this.hostSeamlessAuthMode) {
            // Check if fallback is allowed
            const allowFallback = this.config.hostSeamlessAuth?.allowFallbackToCognito ?? true;
            if (!allowFallback) {
                throw new AuthError('INVALID_CREDENTIALS', 'Manual login not supported in host seamless auth mode. Authentication is handled by the host application.');
            }
        }
        const { username, password: pwd } = credentials;
        return new Promise((resolve, reject) => {
            const cognitoUser = new CognitoUser({
                Username: username,
                Pool: this.userPool,
            });
            const authDetails = new AuthenticationDetails({
                Username: username,
                Password: pwd,
            });
            cognitoUser.authenticateUser(authDetails, {
                onSuccess: (session) => {
                    try {
                        const result = this.createAuthResult(cognitoUser, session);
                        this.storeTokens(session);
                        this.pendingNewPasswordUser = undefined; // Clear any pending state
                        // Notify listeners of successful authentication
                        this.onAuthStateChange(true, 'login');
                        resolve({ success: true, result });
                    }
                    catch (error) {
                        reject(new AuthError('UNKNOWN_ERROR', 'Failed to process authentication result', error));
                    }
                },
                onFailure: (error) => {
                    console.error(`AuthClient: Login failed for user: ${username}`, error);
                    console.error(`AuthClient: Error code: ${error.code}`);
                    console.error(`AuthClient: Error message: ${error.message}`);
                    reject(this.mapCognitoError(error));
                },
                newPasswordRequired: (userAttributes, requiredAttributes) => {
                    // Store the user for completing the new password flow
                    this.pendingNewPasswordUser = cognitoUser;
                    const challenge = {
                        type: 'NEW_PASSWORD_REQUIRED',
                        username,
                        userAttributes,
                        requiredAttributes,
                        session: cognitoUser, // Store the cognito user for later use
                    };
                    resolve({ success: false, challenge });
                },
            });
        });
    }
    /**
     * Complete new password required flow
     */
    async completeNewPassword(request) {
        return new Promise((resolve, reject) => {
            if (!this.pendingNewPasswordUser) {
                reject(new AuthError('INVALID_CREDENTIALS', 'No pending new password user found. Please login first.'));
                return;
            }
            const cognitoUser = this.pendingNewPasswordUser;
            cognitoUser.completeNewPasswordChallenge(request.newPassword, {}, // No additional attributes required
            {
                onSuccess: (session) => {
                    try {
                        const result = this.createAuthResult(cognitoUser, session);
                        this.storeTokens(session);
                        this.pendingNewPasswordUser = undefined; // Clear pending state
                        // Notify listeners of successful authentication
                        this.onAuthStateChange(true, 'login');
                        resolve(result);
                    }
                    catch (error) {
                        reject(new AuthError('UNKNOWN_ERROR', 'Failed to process new password result', error));
                    }
                },
                onFailure: (error) => {
                    this.pendingNewPasswordUser = undefined; // Clear pending state on failure
                    reject(this.mapCognitoError(error));
                },
            });
        });
    }
    /**
     * Sign out the current user
     */
    async logout() {
        await this.logoutWithReason('logout');
    }
    /**
     * Global sign out - invalidates tokens server-side across all devices
     * Use this for shared computers or when you need maximum security
     */
    async globalSignOut() {
        try {
            const currentUser = this.userPool.getCurrentUser();
            if (currentUser) {
                await this.performGlobalSignOut(currentUser);
            }
            // Clear stored tokens
            this.storage.clear();
            // Clear Cognito's internal localStorage to prevent "user not found" issues
            this.clearCognitoInternalStorage();
            // Stop auto-refresh
            this.stopAutoRefresh();
            // Clear any pending user state
            this.pendingNewPasswordUser = undefined;
            // Notify listeners
            this.onAuthStateChange(false, 'logout');
        }
        catch (error) {
            console.error('AuthClient: Error during global sign out:', error);
            throw error;
        }
    }
    /**
     * Perform global sign out with proper error handling
     * This invalidates all tokens server-side across all devices
     */
    async performGlobalSignOut(user) {
        return new Promise((resolve) => {
            user.getSession((err, session) => {
                if (err || !session || !session.isValid()) {
                    console.warn('AuthClient: User session is invalid, skipping global sign out');
                    try {
                        user.signOut();
                        console.log('AuthClient: Performed local signOut for invalid session');
                    }
                    catch (signOutError) {
                        console.error('AuthClient: Local signOut also failed:', signOutError);
                    }
                    resolve();
                    return;
                }
                user.globalSignOut({
                    onSuccess: () => {
                        resolve();
                    },
                    onFailure: (error) => {
                        console.error('AuthClient: Global sign out failed:', error);
                        try {
                            user.signOut();
                            console.warn('AuthClient: Fell back to local signOut due to globalSignOut failure');
                        }
                        catch (signOutError) {
                            console.error('AuthClient: Both globalSignOut and signOut failed:', signOutError);
                        }
                        resolve();
                    },
                });
            });
        });
    }
    /**
     * Sign out the current user with a specific reason
     */
    async logoutWithReason(reason) {
        if (this.hostSeamlessAuthMode) {
            // Host seamless auth doesn't need explicit logout
            // Host application handles session management
            // But we still need to notify listeners
            // Clear cached host token so we don't reuse after host logout
            this.hostTokenManager?.clear();
            this.onAuthStateChange(false, reason);
            return;
        }
        try {
            const currentUser = this.userPool.getCurrentUser();
            if (currentUser) {
                // Use globalSignOut for server-side token invalidation (SECURITY)
                // This ensures tokens become invalid on AWS Cognito servers and can't be used in Postman
                try {
                    await this.performGlobalSignOut(currentUser);
                    console.log(`AuthClient: Global sign out completed - tokens invalidated server-side`);
                }
                catch (globalSignOutError) {
                    console.error(`AuthClient: Global sign out failed, continuing with manual cleanup:`, globalSignOutError);
                    // If globalSignOut fails, we still need to do client cleanup
                    // but the tokens may remain valid server-side (security risk)
                    console.warn('AuthClient: WARNING - Tokens may still be valid server-side due to globalSignOut failure');
                }
            }
            else {
                console.log('AuthClient: No current user found during logout');
            }
            // Clear stored tokens (client-side cleanup)
            this.storage.clear();
            // Clear Cognito's internal localStorage to prevent "user not found" issues
            this.clearCognitoInternalStorage();
            // Stop auto-refresh
            this.stopAutoRefresh();
            // Clear any pending user state
            this.pendingNewPasswordUser = undefined;
            // Notify listeners
            this.onAuthStateChange(false, reason);
        }
        catch (error) {
            console.error('AuthClient: Error during logout:', error);
            throw error;
        }
    }
    /**
     * Clear Cognito's internal localStorage entries that can cause login issues
     * Being more selective to avoid breaking legitimate Cognito functionality
     */
    clearCognitoInternalStorage() {
        if (typeof window === 'undefined' || !window.localStorage) {
            return;
        }
        try {
            const cognitoPrefix = `CognitoIdentityServiceProvider.${this.config.clientId}`;
            const keysToRemove = [];
            const keysToKeep = [];
            // Collect all Cognito keys for this client
            for (let i = 0; i < window.localStorage.length; i++) {
                const key = window.localStorage.key(i);
                if (key && key.startsWith(cognitoPrefix)) {
                    // Only remove session-specific keys, not configuration keys
                    if (key.includes('.accessToken') ||
                        key.includes('.idToken') ||
                        key.includes('.refreshToken') ||
                        key.includes('.clockDrift') ||
                        key.includes('.userData') ||
                        key.includes('.deviceKey')) {
                        keysToRemove.push(key);
                    }
                    else {
                        keysToKeep.push(key);
                    }
                }
            }
            // Remove only session-specific keys
            keysToRemove.forEach((key) => {
                try {
                    window.localStorage.removeItem(key);
                }
                catch (removeError) {
                    console.warn(`Failed to remove Cognito storage key ${key}:`, removeError);
                }
            });
        }
        catch (error) {
            console.warn('AuthClient: Failed to clear Cognito internal storage:', error);
        }
    }
    /**
     * Get the current authenticated user
     */
    async getCurrentUser() {
        return new Promise((resolve) => {
            const user = this.userPool.getCurrentUser();
            if (!user) {
                resolve(null);
                return;
            }
            user.getSession((error, session) => {
                if (error || !session || !session.isValid()) {
                    resolve(null);
                }
                else {
                    resolve(user);
                }
            });
        });
    }
    /**
     * Get current user session
     */
    async getSession() {
        return new Promise((resolve) => {
            const user = this.userPool.getCurrentUser();
            if (!user) {
                resolve(null);
                return;
            }
            user.getSession((error, session) => {
                if (error || !session) {
                    resolve(null);
                }
                else {
                    resolve(session);
                }
            });
        });
    }
    /**
     * Check if user is authenticated
     */
    async isAuthenticated() {
        if (this.hostSeamlessAuthMode) {
            try {
                const token = await this.getAccessToken();
                return !!token;
            }
            catch (error) {
                console.debug('Host seamless auth check failed:', error);
                // Check if fallback is allowed
                const allowFallback = this.config.hostSeamlessAuth?.allowFallbackToCognito ?? true;
                if (!allowFallback)
                    return false;
            }
        }
        // Use existing Cognito implementation or fallback
        const session = await this.getSession();
        return session?.isValid() ?? false;
    }
    /**
     * Get current access token
     */
    async getAccessToken() {
        if (this.hostSeamlessAuthMode) {
            try {
                const token = await this.hostTokenManager.getToken();
                if (token)
                    return token;
            }
            catch (error) {
                this.config.hostSeamlessAuth?.onTokenError?.(error);
                // Check if fallback is allowed
                const allowFallback = this.config.hostSeamlessAuth?.allowFallbackToCognito ?? true;
                if (!allowFallback)
                    return null;
            }
        }
        // Use existing Cognito implementation or fallback
        const session = await this.getSession();
        return session?.getAccessToken().getJwtToken() ?? null;
    }
    /**
     * Get current ID token
     */
    async getIdToken() {
        const session = await this.getSession();
        return session?.getIdToken().getJwtToken() ?? null;
    }
    /**
     * Get current refresh token
     */
    async getRefreshToken() {
        const session = await this.getSession();
        return session?.getRefreshToken().getToken() ?? null;
    }
    /**
     * Refresh the current session
     */
    async refreshToken() {
        if (this.hostSeamlessAuthMode) {
            try {
                // Host seamless auth tokens are refreshed via callback
                const token = await this.getAccessToken();
                if (token)
                    return token;
                throw new AuthError('TOKEN_EXPIRED', 'Failed to refresh host seamless auth token');
            }
            catch (error) {
                console.warn('Host seamless auth refresh failed, falling back to Cognito:', error);
                // Check if fallback is allowed
                const allowFallback = this.config.hostSeamlessAuth?.allowFallbackToCognito ?? true;
                if (!allowFallback)
                    throw error;
            }
        }
        return new Promise((resolve, reject) => {
            const user = this.userPool.getCurrentUser();
            if (!user) {
                reject(new AuthError('USER_NOT_FOUND', 'No current user found'));
                return;
            }
            user.getSession((error, session) => {
                if (error || !session) {
                    reject(new AuthError('TOKEN_EXPIRED', 'Failed to get current session', error || undefined));
                    return;
                }
                const refreshToken = session.getRefreshToken();
                user.refreshSession(refreshToken, (refreshError, newSession) => {
                    if (refreshError) {
                        reject(this.mapCognitoError(refreshError));
                    }
                    else {
                        this.storeTokens(newSession);
                        resolve(newSession.getAccessToken().getJwtToken());
                    }
                });
            });
        });
    }
    /**
     * Get user information
     */
    async getUserInfo() {
        if (this.hostSeamlessAuthMode) {
            try {
                const token = await this.getAccessToken();
                if (token) {
                    // Decode JWT to extract user info (same format as Cognito tokens)
                    const payload = JSON.parse(atob(token.split('.')[1]));
                    return extractUserInfo(payload);
                }
            }
            catch (error) {
                console.error('Failed to get host seamless auth user info:', error);
                // Check if fallback is allowed
                const allowFallback = this.config.hostSeamlessAuth?.allowFallbackToCognito ?? true;
                if (!allowFallback)
                    return null;
            }
        }
        // Use existing Cognito implementation or fallback
        const token = await this.getIdToken();
        if (!token) {
            return null;
        }
        try {
            const config = {
                userPoolId: this.config.userPoolId,
                clientId: this.config.clientId,
                region: this.config.region,
            };
            const claims = await decodeToken(token, config, 'id');
            return extractUserInfo(claims);
        }
        catch (error) {
            throw new AuthError('INVALID_TOKEN', 'Failed to get user info', error);
        }
    }
    /**
     * Change user password
     */
    async changePassword(request) {
        return new Promise((resolve, reject) => {
            const user = this.userPool.getCurrentUser();
            if (!user) {
                reject(new AuthError('USER_NOT_FOUND', 'No current user found'));
                return;
            }
            user.getSession((error, session) => {
                if (error || !session) {
                    reject(new AuthError('TOKEN_EXPIRED', 'User session expired', error || undefined));
                    return;
                }
                user.changePassword(request.oldPassword, request.newPassword, (changeError) => {
                    if (changeError) {
                        reject(this.mapCognitoError(changeError));
                    }
                    else {
                        resolve();
                    }
                });
            });
        });
    }
    /**
     * Initiate forgot password flow
     */
    async forgotPassword(request) {
        return new Promise((resolve, reject) => {
            const cognitoUser = new CognitoUser({
                Username: request.username,
                Pool: this.userPool,
            });
            cognitoUser.forgotPassword({
                onSuccess: () => resolve(),
                onFailure: (error) => reject(this.mapCognitoError(error)),
            });
        });
    }
    /**
     * Confirm forgot password with code
     */
    async confirmPassword(request) {
        return new Promise((resolve, reject) => {
            const cognitoUser = new CognitoUser({
                Username: request.username,
                Pool: this.userPool,
            });
            cognitoUser.confirmPassword(request.confirmationCode, request.newPassword, {
                onSuccess: () => resolve(),
                onFailure: (error) => reject(this.mapCognitoError(error)),
            });
        });
    }
    /**
     * Clear stored session
     */
    async clearSession() {
        this.clearTokens();
        this.stopAutoRefresh();
    }
    /**
     * Decode a token and return claims
     */
    async decodeToken(token, tokenUse = 'access') {
        const config = {
            userPoolId: this.config.userPoolId,
            clientId: this.config.clientId,
            region: this.config.region,
        };
        return decodeToken(token, config, tokenUse);
    }
    /**
     * Check if a token is expired
     */
    async isTokenExpired(token, tokenUse = 'access') {
        const config = {
            userPoolId: this.config.userPoolId,
            clientId: this.config.clientId,
            region: this.config.region,
        };
        return isTokenExpired(token, config, this.config.refreshThreshold, tokenUse);
    }
    /**
     * Get token claims
     */
    async getTokenClaims(token, tokenUse = 'access') {
        const config = {
            userPoolId: this.config.userPoolId,
            clientId: this.config.clientId,
            region: this.config.region,
        };
        return decodeToken(token, config, tokenUse);
    }
    /**
     * Introspect the current token by validating it with Cognito
     * @returns Promise that resolves to true if token is valid, false otherwise
     */
    async introspectToken() {
        try {
            const currentUser = this.userPool.getCurrentUser();
            if (!currentUser) {
                return false;
            }
            // Get the current session
            const session = await new Promise((resolve, reject) => {
                currentUser.getSession((error, session) => {
                    if (error || !session) {
                        reject(error || new Error('No session'));
                    }
                    else {
                        resolve(session);
                    }
                });
            });
            if (!session.isValid()) {
                return false;
            }
            // Validate the token by making a call to Cognito
            try {
                await new Promise((resolve, reject) => {
                    currentUser.getUserAttributes((error, attributes) => {
                        if (error) {
                            reject(error);
                        }
                        else {
                            const attrMap = {};
                            attributes?.forEach((attr) => {
                                attrMap[attr.getName()] = attr.getValue();
                            });
                            resolve(attrMap);
                        }
                    });
                });
                return true;
            }
            catch {
                return false;
            }
        }
        catch {
            return false;
        }
    }
    /**
     * Ensure token is valid based on configuration
     * @returns Promise that resolves to true if token is valid, false otherwise
     */
    async ensureTokenValid() {
        const config = this.config.introspection;
        if (!config?.enabled) {
            return true; // If introspection is disabled, assume valid
        }
        // Check if we should validate based on activity
        if (config.onActivity && this.lastActivity) {
            const timeSinceActivity = Date.now() - this.lastActivity;
            const activityThreshold = 5 * 60 * 1000; // 5 minutes
            if (timeSinceActivity > activityThreshold) {
                return true; // User inactive, skip validation
            }
        }
        // Check if we should validate based on visibility
        if (config.onVisibilityChange && !this.isVisible) {
            return true; // Tab not visible, skip validation
        }
        // Perform the actual validation
        const isValid = await this.introspectToken();
        if (!isValid) {
            console.warn('Introspection: Token invalid - logging out user');
            try {
                await this.logoutWithReason('token_revoked');
            }
            catch (logoutError) {
                console.error('Introspection: Error during logout:', logoutError);
            }
        }
        return isValid;
    }
    /**
     * Dispose of the client and clean up resources
     */
    dispose() {
        this.stopAutoRefresh();
        this.stopIntrospection();
    }
    /**
     * Purge cached host token (host seamless auth mode only)
     */
    purgeHostTokenCache() {
        if (this.hostSeamlessAuthMode) {
            this.hostTokenManager?.clear();
            console.log('AuthClient: purged cached host token');
        }
    }
    // Private methods
    createAuthResult(user, session) {
        return {
            user,
            session,
            accessToken: session.getAccessToken().getJwtToken(),
            refreshToken: session.getRefreshToken().getToken(),
            idToken: session.getIdToken().getJwtToken(),
        };
    }
    storeTokens(session) {
        this.storage.setItem('accessToken', session.getAccessToken().getJwtToken());
        this.storage.setItem('refreshToken', session.getRefreshToken().getToken());
        this.storage.setItem('idToken', session.getIdToken().getJwtToken());
    }
    clearTokens() {
        this.storage.clear();
    }
    startAutoRefresh() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
        }
        this.refreshTimer = setInterval(async () => {
            try {
                if (this.hostSeamlessAuthMode) {
                    // Trigger host token refresh if near expiry; HostTokenManager avoids spamming
                    await this.getAccessToken();
                }
                else {
                    const isAuthenticated = await this.isAuthenticated();
                    if (isAuthenticated) {
                        const session = await this.getSession();
                        if (session) {
                            const expiresIn = session.getAccessToken().getExpiration() * 1000 - Date.now();
                            if (expiresIn <= this.config.refreshThreshold * 1000) {
                                await this.refreshToken();
                            }
                        }
                    }
                }
            }
            catch (error) {
                console.warn('Auto-refresh failed:', error);
            }
        }, 60000); // Check every minute
    }
    stopAutoRefresh() {
        if (this.refreshTimer) {
            clearInterval(this.refreshTimer);
            this.refreshTimer = undefined;
        }
    }
    startIntrospection() {
        if (!this.config.introspection?.enabled || this.hostSeamlessAuthMode) {
            return;
        }
        // Set up activity tracking
        if (this.config.introspection.onActivity &&
            typeof document !== 'undefined') {
            const updateActivity = () => {
                this.lastActivity = Date.now();
            };
            ['click', 'keypress', 'scroll', 'mousemove'].forEach((event) => {
                document.addEventListener(event, updateActivity, { passive: true });
                this.activityListeners.push({ event, handler: updateActivity });
            });
        }
        // Set up visibility tracking
        if (this.config.introspection.onVisibilityChange &&
            typeof document !== 'undefined') {
            this.visibilityHandler = () => {
                this.isVisible = !document.hidden;
            };
            document.addEventListener('visibilitychange', this.visibilityHandler);
        }
        // Set up periodic checking
        if (this.introspectionTimer) {
            clearInterval(this.introspectionTimer);
        }
        const interval = this.config.introspection.interval * 1000;
        this.introspectionTimer = setInterval(async () => {
            try {
                const isValid = await this.introspectToken();
                if (!isValid) {
                    console.warn('Introspection: Token invalid - logging out user');
                    try {
                        await this.logoutWithReason('token_revoked');
                    }
                    catch (logoutError) {
                        console.error('Introspection: Error during logout:', logoutError);
                    }
                }
            }
            catch (error) {
                console.warn('Token introspection failed:', error);
                // If introspection itself fails, also logout as a safety measure
                console.warn('Introspection: Exception during periodic check - logging out user');
                try {
                    await this.logoutWithReason('session_expired');
                }
                catch (logoutError) {
                    console.error('Introspection: Error during logout after exception:', logoutError);
                }
            }
        }, interval);
    }
    stopIntrospection() {
        // Clear the periodic timer
        if (this.introspectionTimer) {
            clearInterval(this.introspectionTimer);
            this.introspectionTimer = undefined;
        }
        // Remove activity event listeners
        if (typeof document !== 'undefined') {
            this.activityListeners.forEach(({ event, handler }) => {
                document.removeEventListener(event, handler);
            });
            this.activityListeners = [];
            // Remove visibility change listener
            if (this.visibilityHandler) {
                document.removeEventListener('visibilitychange', this.visibilityHandler);
                this.visibilityHandler = undefined;
            }
        }
    }
    mapCognitoError(error) {
        const message = error.message || 'Unknown authentication error';
        switch (error.code) {
            case 'NotAuthorizedException':
                return new AuthError('INVALID_CREDENTIALS', message, error);
            case 'UserNotFoundException':
                return new AuthError('USER_NOT_FOUND', message, error);
            case 'UserNotConfirmedException':
                return new AuthError('USER_NOT_CONFIRMED', message, error);
            case 'PasswordResetRequiredException':
                return new AuthError('PASSWORD_RESET_REQUIRED', message, error);
            case 'NetworkError':
                return new AuthError('NETWORK_ERROR', message, error);
            case 'TokenExpiredError':
                return new AuthError('TOKEN_EXPIRED', message, error);
            default:
                return new AuthError('UNKNOWN_ERROR', message, error);
        }
    }
}
//# sourceMappingURL=AuthClient.js.map